import { useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { ChatExperienceContext, type ChatExperienceContextValue } from './chat-experience';

export function ChatExperienceProvider({ children }: PropsWithChildren) {
  const [activeDocument, setActiveDocument] = useState<ChatDocumentDTO | null>(null);
  const [activeSourceReference, setActiveSourceReference] = useState<ChatExperienceContextValue['activeSourceReference']>(null);
  const [activeSourceReferences, setActiveSourceReferences] = useState<ChatExperienceContextValue['activeSourceReferences']>([]);
  const [isDocumentOpen, setIsDocumentOpen] = useState(false);

  const clearDocument = useCallback(() => {
    setActiveDocument(null);
    setActiveSourceReference(null);
    setActiveSourceReferences([]);
    setIsDocumentOpen(false);
  }, []);

  const closeDocument = useCallback(() => {
    setIsDocumentOpen(false);
  }, []);

  const openDocument = useCallback((
    document: ChatDocumentDTO,
    reference?: ChatExperienceContextValue['activeSourceReference'],
    references?: ChatExperienceContextValue['activeSourceReferences'] | null,
  ) => {
    setActiveDocument(document);
    setActiveSourceReference(reference ?? null);
    setActiveSourceReferences(references ?? []);
    setIsDocumentOpen(true);
  }, []);

  const patchDocument = useCallback<ChatExperienceContextValue['patchDocument']>((documentId, patch) => {
    setActiveDocument((current) => {
      if (!current || current.documentId !== documentId) {
        return current;
      }

      if (typeof patch === 'function') {
        return patch(current);
      }

      return { ...current, ...patch };
    });
  }, []);

  const value = useMemo<ChatExperienceContextValue>(() => ({
    activeDocument,
    activeSourceReference,
    activeSourceReferences,
    clearDocument,
    closeDocument,
    isDocumentOpen,
    openDocument,
    patchDocument,
    setActiveSourceReference,
  }), [
    activeDocument,
    activeSourceReference,
    activeSourceReferences,
    clearDocument,
    closeDocument,
    isDocumentOpen,
    openDocument,
    patchDocument,
  ]);

  return (
    <ChatExperienceContext.Provider value={value}>
      {children}
    </ChatExperienceContext.Provider>
  );
}
